import PropTypes from 'prop-types';
// @mui
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// components
import Iconify from 'src/components/iconify';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { LoadingButton } from '@mui/lab';
import { CircularProgress, List, ListItem } from '@mui/material';
import Label from 'src/components/label';
import { useState } from 'react';
import { getRequest } from 'src/services';
import { endpoints } from 'src/utils/axios';
import { ChatAdminUserModal } from './chat-modal';
import { AssignTicketModal } from './assign-modal';
import { useAuthContext } from 'src/auth/hooks';

export default function UserTableRow({ row, selected, onEditRow, onSelectRow, onDeleteRow, deleteLoading, reload, adminUsers }) {
    const { name, description, email, files, status } = row;
    const [ loading, setLoading ] = useState(false);
    const [ anchorEl, setAnchorEl ] = useState(null);

    const confirm = useBoolean();
    const chat = useBoolean();
    const assign = useBoolean();
    const popover = usePopover();

    const { user } = useAuthContext();
    const userPermissions = user?.roles?.flatMap(role => role?.permissions?.map(perm => perm?.name)) || [];
    const hasPermission = (keyword) => userPermissions?.some(permission => permission === keyword);
    const canDelete = hasPermission('support_tickets.delete');
    const canEdit = hasPermission('support_tickets.edit');
    const canAssign = hasPermission('support_tickets.assign');
    console.log("🚀 ~support tieckt permisions ", canDelete, canEdit, canAssign)

    const statusOptions = [
        { id: 1, name: "OPEN", color: "primary" },
        { id: 2, name: "PENDING", color: "warning" },
        { id: 3, name: "ASSIGNED", color: "info" },
        { id: 4, name: "IN PROGRESS", color: "primary" },
        { id: 5, name: "RESOLVED", color: "success" },
        { id: 6, name: "CLOSED", color: "error" }
    ];

    const handleStatusClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };
    const handleStatusChange = async (selectedStatus) => {
        setLoading(true);
        try {
            await getRequest(`${endpoints.admin_dashboard.support_tickets.change_status}/${row.id}/change-status/${selectedStatus.id}`);
            reload();
        } catch (error) {
            console.error('Error toggling status:', error);
        } finally {
            setLoading(false);
            handleClose();
        }
    };
    return (
        <>
            <TableRow hover selected={ selected }>
                <TableCell padding="checkbox">
                    <Checkbox checked={ selected } onClick={ onSelectRow } />
                </TableCell>
                <TableCell sx={ { whiteSpace: 'nowrap' } }>{ name }</TableCell>
                <TableCell sx={ { whiteSpace: 'nowrap' } }>{ description }</TableCell>
                <TableCell sx={ { whiteSpace: 'nowrap' } }>{ email }</TableCell>

                {/* Status Cell - Click to Open Popover */ }
                <TableCell sx={ { justifyContent: "center", cursor: "pointer" } } onClick={ handleStatusClick }>
                    { loading ? <CircularProgress size={ 24 } color="success" /> :
                        <Label
                            variant="soft"
                            color={ statusOptions.find(s => s.id === status?.value)?.color || 'default' }
                            style={ { cursor: "pointer" } }
                        >
                            { status?.name }
                        </Label>
                    }
                </TableCell>


                <TableCell align="left">
                    { files?.length > 0 ? (
                        <a href={ files[ 0 ].url } target="_blank" rel="noopener noreferrer">
                            <Label
                                variant="soft"
                                color={ 'primary' }
                                style={ { cursor: "pointer" } }
                            >
                                { "view file" }
                            </Label>
                        </a>

                    ) : "No File" }
                </TableCell>

                <TableCell align="right" sx={ { px: 1, whiteSpace: 'nowrap' } }>
                    <IconButton color={ popover.open ? 'inherit' : 'default' } onClick={ popover.onOpen }>
                        <Iconify icon="eva:more-vertical-fill" />
                    </IconButton>
                </TableCell>
            </TableRow>

            {/* Status Selection Popover */ }
            <CustomPopover
                open={ Boolean(anchorEl) }
                anchorEl={ anchorEl }
                onClose={ handleClose }
                anchorOrigin={ {
                    vertical: 'bottom',
                    horizontal: 'left',
                } }
                transformOrigin={ {
                    vertical: 'top',
                    horizontal: 'right',
                } }
            >
                <List sx={ { width: 150 } }>
                    { statusOptions.map((statusOption) => (
                        <ListItem
                            key={ statusOption.id }
                            sx={ { cursor: 'pointer', px: 2, py: 1, display: 'flex', alignItems: 'center' } }
                            onClick={ () => handleStatusChange(statusOption) }
                        >
                            <Label variant="soft" color={ statusOption.color } sx={ { flexGrow: 1 } }>
                                { statusOption?.name }
                            </Label>
                        </ListItem>
                    )) }
                </List>
            </CustomPopover >

            <CustomPopover open={ popover.open } onClose={ popover.onClose } arrow="right-top" sx={ { width: 140 } }>
                { canDelete &&
                    <MenuItem onClick={ () => { confirm.onTrue(); popover.onClose(); } } sx={ { color: 'error.main' } }>
                        <Iconify icon="solar:trash-bin-trash-bold" />
                        Delete
                    </MenuItem> }
                {/* { canEdit && <MenuItem onClick={ () => { onEditRow(); popover.onClose(); } }>
                    <Iconify icon="solar:pen-bold" />
                    Edit
                </MenuItem> } */}

                <MenuItem onClick={ () => chat.onTrue() } >
                    <Iconify icon="material-symbols:chat" />
                    Chat
                </MenuItem>
                { canAssign &&
                    <MenuItem onClick={ () => assign.onTrue() }>
                        <Iconify icon="material-symbols:assignment-ind" />
                        Assign Ticket
                    </MenuItem> }
            </CustomPopover>

            {/* //chat modal */ }
            <ChatAdminUserModal
                row={ row }
                open={ chat.value }
                onClose={ chat.onFalse }
            />
            {/* //Assign modal */ }
            <AssignTicketModal
                row={ row }
                open={ assign.value }
                onClose={ assign.onFalse }
                adminUsers={ adminUsers }
            />


            <ConfirmDialog
                open={ confirm.value }
                onClose={ confirm.onFalse }
                title="Delete"
                content="Are you sure want to delete?"
                action={
                    <LoadingButton variant="contained" color="error" loading={ deleteLoading } onClick={ onDeleteRow }>
                        Delete
                    </LoadingButton>
                }
            />
        </>
    );
}
UserTableRow.propTypes = {
    onDeleteRow: PropTypes.func,
    onEditRow: PropTypes.func,
    onSelectRow: PropTypes.func,
    row: PropTypes.object,
    selected: PropTypes.bool,
    reload: PropTypes.func
};
